/* eslint-disable react/jsx-props-no-spreading */
// NB!: Don't include it in atoms/index.js to not include tinymce dependency on each page
import 'tinymce/tinymce';
import './tooltipPlugin';
import 'tinymce/themes/silver';
import './skins/worldathletics.less';

import 'tinymce/plugins/autolink';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/image';
import 'tinymce/plugins/media';
import 'tinymce/plugins/code';
import 'tinymce/plugins/table';

import React from 'react';
import { Editor } from '@tinymce/tinymce-react';

import SelectMedia from '@organisms/AddMediaModal/SelectMedia';
import { MEDIA_TYPE } from '@api/media/constants';
import Collapse from '@atoms/Collapse';
import { getSrc } from '@utils/media';

import contentStyle from './contentStyle';
import ShortcodeModal from './ShortcodeModal';
import CampaignModal from './CampaignModal';
import { configureFocus, imagesUploadHandler } from './utils';
import { TOOLBAR_ITEM, DEFAULT_TOOLBAR, PLUGINS } from './constants';
import s from './styles.module.less';

const RichTextEditor = ({
  name,
  id,
  value,
  onChange,
  height = 300,
  toolbarItems = DEFAULT_TOOLBAR,
  exclude: omit = [],
  include = [],
  initKey,
  showImageSelection = true,
}) => {
  const tinymceRef = React.useRef(null);
  const shortcodes = ShortcodeModal.useShortcodes();
  const campaign = CampaignModal.useCampaign();

  const toolbar = React.useMemo(() => [...toolbarItems, ...include].filter((item) => !omit.includes(item)).join(' '), [
    omit,
    include,
    toolbarItems,
  ]);

  const handleSelect = (selectedMedia) => {
    if (!tinymceRef?.current?.editor) return;

    const src = getSrc({ media: selectedMedia });

    const photographerIconBase64 = `data:image/svg+xml;base64,${btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 480 360">
      <g transform="translate(0,360) scale(0.1,-0.1)" fill="#B2B2B2">
        <path d="M1905 3182 c-126 -44 -187 -100 -253 -233 l-47 -94 -350 -5 c-326 -5
        -354 -6 -401 -26 -147 -60 -246 -180 -274 -331 -14 -76 -14 -1659 0 -1734 31
        -167 173 -313 338 -348 37 -8 476 -11 1490 -11 1543 0 1480 -2 1588 50 104 51
        200 181 224 305 8 43 10 300 8 910 l-3 850 -23 56 c-47 112 -141 206 -252 252
        -52 21 -72 22 -405 27 l-350 5 -46 95 c-54 112 -106 166 -207 213 l-67 32
        -460 2 c-416 3 -465 1 -510 -15z m972 -237 c18 -13 52 -68 93 -150 42 -83 75
        -137 93 -150 27 -19 47 -20 415 -25 438 -6 427 -4 487 -89 l30 -43 3 -836 c2
        -610 0 -848 -8 -876 -17 -56 -54 -102 -104 -125 -42 -21 -51 -21 -1488 -21
        l-1445 0 -44 23 c-29 16 -54 40 -74 72 l-30 48 0 851 c0 844 0 851 21 891 11
        22 40 54 63 70 l43 30 388 5 c369 5 390 6 417 25 18 13 50 64 88 141 33 67 67
        129 75 139 36 43 53 45 512 42 422 -2 438 -3 465 -22z"/>
        <path d="M2300 2451 c-382 -54 -671 -363 -697 -746 -21 -318 161 -627 452
        -766 126 -60 189 -74 350 -74 119 0 151 4 215 23 276 87 480 296 556 572 25
        90 26 299 1 390 -78 288 -300 509 -589 584 -71 18 -216 27 -288 17z m270 -249
        c197 -68 334 -214 385 -407 74 -287 -104 -599 -390 -684 -101 -30 -267 -27
        -360 7 -174 64 -327 240 -364 420 -62 305 135 608 439 677 82 19 215 12 290
        -13z"/>
      </g>
    </svg>
    `)}`;

    const copyright = selectedMedia?.copyright || '';
    const photographer = selectedMedia?.photographer || '';

    let copyrightPhotographerText = '';

    if (copyright && photographer) {
      copyrightPhotographerText = ` (© ${copyright} <img src="${photographerIconBase64}" alt="photographer icon" style="width:16px;vertical-align:middle;display:inline;" /> ${photographer})`;
    } else if (copyright) {
      copyrightPhotographerText = ` (© ${copyright})`;
    } else if (photographer) {
      copyrightPhotographerText = ` (<img src="${photographerIconBase64}" alt="photographer icon" style="width:16px;vertical-align:middle;display:inline;" /> ${photographer})`;
    }

    const footerText = `
      <p style="font-size:0.9em;color:#B2B2B2;text-align:right;margin:-15px 0 0px;">
        ${selectedMedia?.title || ''}
        ${copyrightPhotographerText}
      </p>
`;

    const content = `
    <div>
      <img src="${src}" alt="${selectedMedia?.title || ''}" style="max-width:900px;width:100%;display:block;" />
      <div>${footerText}</div>
    </div>
    <br />`;

    tinymceRef.current.editor.insertContent(content);
  };

  return (
    <>
      <Editor
        textareaName={name}
        id={initKey || id}
        plugins={PLUGINS}
        toolbar={toolbar}
        value={value}
        ref={tinymceRef}
        init={{
          skin: false,
          content_style: contentStyle,
          height,
          images_upload_handler: imagesUploadHandler,
          menubar: false,
          statusbar: false,
          setup: (editor) => {
            configureFocus({ editor });
            shortcodes.configure({ editor });
            campaign.configure({ editor });
          },
        }}
        onEditorChange={onChange}
      />
      {showImageSelection && (
        <Collapse className={s.collapse}>
          <Collapse.Panel header="Select Media Image" className={s.collapseHeader}>
            <SelectMedia type={MEDIA_TYPE.IMAGE} onSelect={handleSelect} className={s.collapseContent} />
          </Collapse.Panel>
        </Collapse>
      )}
      <ShortcodeModal
        {...shortcodes.modal}
        type={shortcodes.type}
        onSubmit={(str) => tinymceRef.current?.editor.insertContent(str)}
      />
      <CampaignModal
        {...campaign.modal}
        type={campaign.type}
        onSubmit={(str) => tinymceRef.current?.editor.insertContent(str)}
      />
    </>
  );
};

RichTextEditor.TOOLBAR_ITEM = TOOLBAR_ITEM;

export default RichTextEditor;
